"use client";

import React, { useState, useCallback, useEffect, useMemo } from "react";
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  addEdge,
  useNodesState,
  useEdgesState,
  Panel,
  BackgroundVariant,
  type Edge,
  type Node,
  type OnConnect,
} from "@xyflow/react";
import {
  BrainCircuit,
  Download,
  Maximize2,
  Minimize2,
  Trash2,
  Upload,
} from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import type { IdeaNodeData, NodeKind, DbSchema } from "./types";
import { uid, emptyNodeData, NODE_META } from "./constants";
import { loadFlow, saveFlow } from "./persistence";
import { nodeTypes } from "./IdeaFlowNode";
import { edgeTypes, defaultEdgeOptions } from "./DeletableEdge";
import { EditPanel } from "./EditPanel";
import { AddNodeButton } from "./AddNodeButton";
import { PromptModal } from "./PromptModal";
import { exampleFlow } from "./example-flow";
import { flowDocumentSchema } from "./flow-document";

export function FlowCanvas({
  dbSchema,
  onDbSchemaChange,
}: {
  dbSchema: DbSchema;
  onDbSchemaChange: (s: DbSchema) => void;
}) {
  const [nodes, setNodes, onNodesChange] = useNodesState<Node<IdeaNodeData>>(
    [],
  );
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [promptNodeId, setPromptNodeId] = useState<string | null | "overview">(
    null,
  );
  const fileRef = React.useRef<HTMLInputElement>(null);
  const [hydrated, setHydrated] = useState(false);
  const [storageBlocked, setStorageBlocked] = useState(false);
  const [notice, setNotice] = useState("");
  const [query, setQuery] = useState("");

  // Hydrate from localStorage after client mount
  useEffect(() => {
    try {
      const saved = loadFlow();
      setNodes(saved.nodes);
      setEdges(saved.edges);
    } catch (error) {
      // Browser storage is only available after hydration; keep corrupt data untouched.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setStorageBlocked(true);
      setNotice((error as Error).message);
    }
    setHydrated(true);
  }, [setNodes, setEdges]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isFullscreen) setIsFullscreen(false);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isFullscreen]);

  useEffect(() => {
    if (!hydrated || storageBlocked) return;
    try {
      saveFlow(nodes, edges);
    } catch {
      // Surface external storage failures without discarding the editable flow.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setNotice(
        "Speichern im Browser fehlgeschlagen. Bitte den Flow als JSON exportieren.",
      );
    }
  }, [nodes, edges, hydrated, storageBlocked]);

  const selectedNode = useMemo(
    () =>
      selectedNodeId
        ? ((nodes.find((n) => n.id === selectedNodeId) as
            | Node<IdeaNodeData>
            | undefined) ?? null)
        : null,
    [nodes, selectedNodeId],
  );

  const onConnect: OnConnect = useCallback(
    (params) =>
      setEdges((eds) => addEdge({ ...params, ...defaultEdgeOptions }, eds)),
    [setEdges],
  );

  const addNode = useCallback(
    (kind: NodeKind) => {
      const id = uid();
      const newNode: Node<IdeaNodeData> = {
        id,
        type: "ideaNode",
        position: { x: 80 + Math.random() * 400, y: 80 + Math.random() * 300 },
        data: emptyNodeData(kind),
      };
      setNodes((nds) => [...nds, newNode]);
      setSelectedNodeId(id);
    },
    [setNodes],
  );

  const updateNodeData = useCallback(
    (nodeId: string, data: IdeaNodeData) => {
      setNodes((nds) => nds.map((n) => (n.id === nodeId ? { ...n, data } : n)));
    },
    [setNodes],
  );

  const deleteNode = useCallback(
    (nodeId: string) => {
      setNodes((nds) => nds.filter((n) => n.id !== nodeId));
      setEdges((eds) =>
        eds.filter((e) => e.source !== nodeId && e.target !== nodeId),
      );
      setSelectedNodeId(null);
    },
    [setNodes, setEdges],
  );

  const handleExport = () => {
    const blob = new Blob(
      [JSON.stringify({ version: 2, nodes, edges, dbSchema }, null, 2)],
      { type: "application/json" },
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "idea-flow.json";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5_000_000) {
      setNotice("Die Datei darf höchstens 5 MB groß sein.");
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const data = flowDocumentSchema.parse(
          JSON.parse(ev.target?.result as string),
        );
        if (
          nodes.length &&
          !window.confirm("Den aktuellen Flow durch den Import ersetzen?")
        )
          return;
        setNodes(data.nodes ?? []);
        setEdges(data.edges ?? []);
        if (data.dbSchema) onDbSchemaChange(data.dbSchema);
        setSelectedNodeId(null);
        setNotice("Flow importiert.");
      } catch {
        setNotice(
          "Import fehlgeschlagen: Bitte eine gültige Idea-Scratcher-Datei auswählen. Dein Flow bleibt erhalten.",
        );
      }
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const clearAll = () => {
    if (
      nodes.length &&
      !window.confirm("Alle Schritte und Verbindungen dieses Flows löschen?")
    )
      return;
    setNodes([]);
    setEdges([]);
    setSelectedNodeId(null);
  };

  const containerCls = isFullscreen
    ? "fixed inset-0 z-50 flex overflow-hidden bg-white dark:bg-zinc-950"
    : "relative flex h-155 overflow-hidden";

  return (
    <div className={containerCls}>
      <div className="flex-1">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          defaultEdgeOptions={defaultEdgeOptions}
          onNodeClick={(_, node) => setSelectedNodeId(node.id)}
          onPaneClick={() => setSelectedNodeId(null)}
          fitView
          fitViewOptions={{ padding: 0.3 }}
          deleteKeyCode="Delete"
          className="bg-gray-50 dark:bg-zinc-950"
        >
          <Background
            variant={BackgroundVariant.Dots}
            gap={20}
            size={1}
            color="#d1d5db"
          />
          <Controls className="rounded-xl! border! border-gray-200! bg-white! shadow-sm! dark:border-white/10! dark:bg-zinc-900!" />
          <MiniMap
            nodeColor={(n) =>
              NODE_META[(n.data as IdeaNodeData).nodeKind]?.handleColor ??
              "#94a3b8"
            }
            className="rounded-xl! border! border-gray-200! bg-white! dark:border-white/10! dark:bg-zinc-900!"
          />
          {/* Toolbar */}
          <Panel position="top-left">
            <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-gray-200 bg-white/95 p-2 shadow-sm backdrop-blur dark:border-white/10 dark:bg-zinc-900/95">
              {(["signal", "action", "state"] as NodeKind[]).map((k) => (
                <AddNodeButton key={k} kind={k} onAdd={addNode} />
              ))}
              <button
                className="rounded-xl px-3 py-1.5 text-xs text-gray-500 hover:bg-gray-100"
                onClick={() => {
                  const example = exampleFlow();
                  setNodes((nds) => [...nds, ...example.nodes]);
                  setEdges((eds) => [...eds, ...example.edges]);
                }}
              >
                + Beispiel
              </button>
              <div className="mx-1 h-5 w-px bg-gray-200 dark:bg-white/10" />
              <button
                onClick={() => setPromptNodeId("overview")}
                className="flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-xs text-gray-500 hover:bg-gray-100 dark:hover:bg-white/5"
              >
                <BrainCircuit className="h-3.5 w-3.5" /> KI-Prompt
              </button>
              <button
                onClick={handleExport}
                className="flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-xs text-gray-500 hover:bg-gray-100 dark:hover:bg-white/5"
              >
                <Download className="h-3.5 w-3.5" /> Export
              </button>
              <button
                onClick={() => fileRef.current?.click()}
                className="flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-xs text-gray-500 hover:bg-gray-100 dark:hover:bg-white/5"
              >
                <Upload className="h-3.5 w-3.5" /> Import
              </button>
              <button
                onClick={clearAll}
                className="flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-xs text-gray-500 hover:bg-red-50 hover:text-red-400 dark:hover:bg-red-500/10"
              >
                <Trash2 className="h-3.5 w-3.5" /> Leeren
              </button>
              <div className="mx-1 h-5 w-px bg-gray-200 dark:bg-white/10" />
              <button
                onClick={() => setIsFullscreen((f) => !f)}
                title={isFullscreen ? "Vollbild beenden (Esc)" : "Vollbild"}
                className="flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-xs text-gray-500 hover:bg-gray-100 dark:hover:bg-white/5"
              >
                {isFullscreen ? (
                  <Minimize2 className="h-3.5 w-3.5" />
                ) : (
                  <Maximize2 className="h-3.5 w-3.5" />
                )}
                {isFullscreen ? "Verkleinern" : "Vollbild"}
              </button>
              <input
                ref={fileRef}
                type="file"
                accept=".json"
                className="hidden"
                onChange={handleImport}
              />
            </div>
            <div className="max-w-80 mt-2 rounded-xl border border-gray-200 bg-white p-2 dark:border-white/10 dark:bg-zinc-900">
              <input
                aria-label="Flow durchsuchen"
                placeholder="Schritte, Signale, Prompts durchsuchen…"
                className="w-full bg-transparent px-2 py-1 text-xs outline-none"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              {query.trim() && (
                <div className="max-h-40 overflow-auto">
                  {nodes
                    .filter((n) =>
                      JSON.stringify(n.data)
                        .toLowerCase()
                        .includes(query.trim().toLowerCase()),
                    )
                    .map((n) => (
                      <button
                        key={n.id}
                        onClick={() => setSelectedNodeId(n.id)}
                        className="block w-full rounded px-2 py-2 text-left text-xs hover:bg-gray-100 dark:hover:bg-white/10"
                      >
                        {NODE_META[n.data.nodeKind].label} ·{" "}
                        {n.data.title || "Ohne Titel"}
                      </button>
                    ))}
                  {!nodes.some((n) =>
                    JSON.stringify(n.data)
                      .toLowerCase()
                      .includes(query.trim().toLowerCase()),
                  ) && (
                    <p className="px-2 py-2 text-xs text-gray-500">
                      Keine passenden Schritte.
                    </p>
                  )}
                </div>
              )}
            </div>
          </Panel>
          {notice && (
            <Panel position="bottom-center">
              <p
                role="status"
                className="max-w-md rounded-xl border bg-white p-3 text-xs text-gray-700 shadow dark:bg-zinc-900 dark:text-gray-200"
              >
                {notice}
              </p>
            </Panel>
          )}
          {/* Empty state hint */}
          {nodes.length === 0 && (
            <Panel position="top-right">
              <div className="max-w-55 rounded-2xl border border-dashed border-gray-200 bg-white/90 px-5 py-4 text-center text-sm text-gray-400 backdrop-blur dark:border-white/10 dark:bg-zinc-900/90">
                <p className="font-medium text-gray-600 dark:text-gray-300">
                  Canvas ist leer
                </p>
                <p className="mt-1 text-xs leading-relaxed">
                  Klicke oben links auf einen Typ um einen Node hinzuzufügen.
                  Verbinde Nodes per Drag vom Handle (farbiger Kreis am
                  Node-Rand).
                </p>
              </div>
            </Panel>
          )}
        </ReactFlow>
      </div>

      {/* Node edit dialog */}
      <AnimatePresence>
        {selectedNode && (
          <motion.div
            key="dialog-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="absolute inset-0 z-40 flex items-center justify-center bg-black/40 backdrop-blur-sm"
            onClick={(e) => {
              if (e.target === e.currentTarget) setSelectedNodeId(null);
            }}
          >
            <motion.div
              key={selectedNode.id}
              initial={{ scale: 0.95, opacity: 0, y: 12 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 12 }}
              transition={{ duration: 0.18 }}
              className="mx-4 flex h-[92%] w-full max-w-5xl flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl dark:border-white/10 dark:bg-zinc-950"
            >
              <EditPanel
                node={selectedNode}
                onChange={(data) => updateNodeData(selectedNode.id, data)}
                onDelete={() => deleteNode(selectedNode.id)}
                onClose={() => setSelectedNodeId(null)}
                onGeneratePrompt={() => {
                  setSelectedNodeId(null);
                  setPromptNodeId(selectedNode.id);
                }}
                dbSchema={dbSchema}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* KI-Prompt modal */}
      <AnimatePresence>
        {promptNodeId !== null && (
          <PromptModal
            nodes={nodes}
            edges={edges}
            dbSchema={dbSchema}
            initialNodeId={promptNodeId === "overview" ? null : promptNodeId}
            onClose={() => setPromptNodeId(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
